/* Cart drawer product recommendations (<fendo-cart-recommendations>).
 * Moved out of sections/fendo-cart-drawer.liquid so the browser caches it
 * instead of re-downloading it inside the HTML of every page.
 * The button label comes from window.fendoStrings (snippets/fendo-head.liquid).
 */
if (!customElements.get('fendo-cart-recommendations')) {
  const storageKey = 'fendo-cart-recommendation-order-v1';
  let recentProducts = [];
  let knownQuantities = null;
  try {
    const stored = JSON.parse(sessionStorage.getItem(storageKey) || '[]');
    if (Array.isArray(stored)) recentProducts = stored.filter(id => /^\d+$/.test(String(id))).map(String);
  } catch (_) {}

  const remember = (ids) => {
    const unique = [...new Set(ids.map(String).filter(id => /^\d+$/.test(id)))];
    recentProducts = [...unique, ...recentProducts.filter(id => !unique.includes(id))].slice(0, 100);
    try { sessionStorage.setItem(storageKey, JSON.stringify(recentProducts)); } catch (_) {}
  };

  document.addEventListener('variant:add', (event) => {
    if (!event.detail?.cart || !Array.isArray(event.detail?.items)) return;
    const cartIds = new Set(event.detail.cart.items.map(item => String(item.product_id)));
    remember(event.detail.items.map(item => item.product_id).filter(id => cartIds.has(String(id))));
  }, true);

  document.addEventListener('cart:change', (event) => {
    const cart = event.detail?.cart;
    if (!Array.isArray(cart?.items)) return;
    const quantities = new Map();
    for (const item of cart.items) {
      const id = String(item.product_id);
      quantities.set(id, (quantities.get(id) || 0) + Number(item.quantity || 0));
    }
    recentProducts = recentProducts.filter(id => quantities.has(id));
    if (event.detail.baseEvent === 'variant:add') {
      const added = cart.items.filter(item => knownQuantities &&
        (quantities.get(String(item.product_id)) || 0) > (knownQuantities.get(String(item.product_id)) || 0));
      if (added.length) remember(added.map(item => item.product_id));
      else if (!recentProducts.length && cart.items.length) remember([cart.items[0].product_id]);
    }
    knownQuantities = quantities;
    try { sessionStorage.setItem(storageKey, JSON.stringify(recentProducts)); } catch (_) {}
    document.querySelectorAll('fendo-cart-recommendations').forEach(element => {
      element.setAttribute('cart-product-ids', [...quantities.keys()].join(','));
      element.load();
    });
  }, true);

  class FendoCartRecommendations extends HTMLElement {
    constructor() {
      super();
      this.abortController = null;
      this.mediaQuery = window.matchMedia('(min-width: 1000px)');
      this.onMediaChange = () => this.load();
      this.cartDrawer = null;
    }

    connectedCallback() {
      this.cartDrawer = this.closest('cart-drawer');
      this.mediaQuery.addEventListener?.('change', this.onMediaChange);
      this.load();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.mediaQuery.removeEventListener?.('change', this.onMediaChange);

      if (this.getAttribute('media') === 'desktop') {
        this.cartDrawer?.classList.remove('fendo-has-cross-sell');
      }
    }

    async load() {
      this.abortController?.abort();
      const requestId = this.requestId = (this.requestId || 0) + 1;
      const cartIds = new Set((this.getAttribute('cart-product-ids') || '').split(',').map(id => id.trim()).filter(Boolean));
      const fallbackId = this.getAttribute('product-id');
      const productId = recentProducts.find(id => cartIds.has(id)) ||
        (cartIds.has(fallbackId) ? fallbackId : [...cartIds][0]);
      const limit = Number(this.getAttribute('limit') || 3);
      const mode = this.getAttribute('media') || 'desktop';
      const list = this.querySelector('[data-fendo-recommendations-list]');
      const shell = this.closest('[data-fendo-cross-sell-shell]');
      const isDesktop = this.mediaQuery.matches;
      const shouldLoad = mode === 'desktop' ? isDesktop : !isDesktop;

      if (!list || !shell) return;
      shell.hidden = true;
      list.replaceChildren();
      if (mode === 'desktop') this.cartDrawer?.classList.remove('fendo-has-cross-sell');
      if (!productId || !cartIds.size) return;

      if (!shouldLoad) {
        shell.hidden = true;

        if (mode === 'desktop') {
          this.cartDrawer?.classList.remove('fendo-has-cross-sell');
        }

        return;
      }

      this.abortController = new AbortController();
      const controller = this.abortController;

      try {
        const url = `${window.Shopify.routes.root}recommendations/products.json?product_id=${encodeURIComponent(productId)}&limit=10&intent=complementary`;
        const response = await fetch(url, {
          signal: controller.signal,
          headers: { Accept: 'application/json' }
        });

        if (!response.ok) throw new Error(`Recommendations request failed: ${response.status}`);

        const data = await response.json();
        if (requestId !== this.requestId || controller.signal.aborted || !this.isConnected) return;
        const products = (data.products || [])
          .filter(product => product.available && !cartIds.has(String(product.id)))
          .slice(0, limit);

        if (!products.length) {
          shell.hidden = true;

          if (mode === 'desktop') {
            this.cartDrawer?.classList.remove('fendo-has-cross-sell');
          }

          return;
        }

        list.innerHTML = products.map((product, index) => this.renderProduct(product, index)).join('');
        shell.hidden = false;

        if (mode === 'desktop') {
          this.cartDrawer?.classList.add('fendo-has-cross-sell');
        }
      } catch (error) {
        if (requestId !== this.requestId || controller.signal.aborted || !this.isConnected) return;
        if (error.name !== 'AbortError') {
          console.warn('[Fendo Cart Cross-sell]', error);
        }
        shell.hidden = true;

        if (mode === 'desktop') {
          this.cartDrawer?.classList.remove('fendo-has-cross-sell');
        }
      }
    }

    renderProduct(product, index) {
      const availableVariant = product.variants?.find(variant => variant.available) || product.variants?.[0];
      if (!availableVariant) return '';

      const productUrl = product.url || `${window.Shopify.routes.root}products/${product.handle}`;
      const imageUrl = this.getImageUrl(product);
      const modalId = `fendo-cart-quick-buy-${product.id}-${index}`;
      const addText = window.fendoStrings?.addToCart || 'In den Warenkorb';
      const title = this.escapeHtml(product.title || '');
      const url = this.escapeAttribute(productUrl);
      const handle = this.escapeAttribute(`${product.handle}?variant=${availableVariant.id}`);

      let quickBuy = '';

      if ((product.variants || []).length === 1) {
        quickBuy = `
          <product-form>
            <form method="post" action="${window.Shopify.routes.root}cart/add" accept-charset="UTF-8">
              <input type="hidden" name="id" value="${availableVariant.id}">
              <input type="hidden" name="quantity" value="1">
              <input type="hidden" name="on_success" value="force_open_drawer">
              <button type="submit" class="@narrow:horizontal-product-card__button link">${this.escapeHtml(addText)}</button>
              <button type="submit" class="@large:horizontal-product-card__button button button--outline button--subdued">${this.escapeHtml(addText)}</button>
            </form>
          </product-form>`;
      } else {
        quickBuy = `
          <quick-buy-modal
            id="${modalId}"
            handle="${handle}"
            class="quick-buy-modal modal modal--lg color-scheme color-scheme--dialog"
          >LOADING</quick-buy-modal>
          <button aria-controls="${modalId}" class="@narrow:horizontal-product-card__button link">${this.escapeHtml(addText)}</button>
          <button aria-controls="${modalId}" class="@large:horizontal-product-card__button button button--outline button--subdued">${this.escapeHtml(addText)}</button>`;
      }

      return `
        <div class="horizontal-product-card">
          ${imageUrl ? `
            <a href="${url}" class="horizontal-product-card__figure" data-instant>
              <img
                src="${this.escapeAttribute(this.resizeImage(imageUrl, 200))}"
                alt="${title}"
                loading="lazy"
                width="200"
                class="horizontal-product-card__image"
              >
            </a>` : ''}

          <div class="horizontal-product-card__info">
            <div class="v-stack gap-1 justify-items-start">
              <a href="${url}" class="product-title" data-instant>${title}</a>
              ${this.renderPrice(product, availableVariant)}
            </div>
            ${quickBuy}
          </div>
        </div>`;
    }

    renderPrice(product, variant) {
      const price = variant.price ?? product.price ?? 0;
      const compareAtPrice = variant.compare_at_price || 0;
      const onSale = compareAtPrice > price;

      return `
        <price-list class="price-list">
          <sale-price class="${onSale ? 'text-on-sale' : 'text-subdued'}">
            ${this.escapeHtml(this.formatMoney(price))}
          </sale-price>
          ${onSale ? `
            <compare-at-price class="text-subdued line-through">
              ${this.escapeHtml(this.formatMoney(compareAtPrice))}
            </compare-at-price>` : ''}
        </price-list>`;
    }

    getImageUrl(product) {
      const image = product.featured_image || product.images?.[0];
      if (!image) return '';
      if (typeof image === 'string') return image;
      return image.src || image.url || '';
    }

    resizeImage(url, width) {
      if (!url) return '';
      const separator = url.includes('?') ? '&' : '?';
      return `${url}${separator}width=${width}`;
    }

    formatMoney(cents) {
      const settings = window.themeVariables?.settings || {};
      const format = settings.currencyCodeEnabled
        ? settings.moneyWithCurrencyFormat
        : settings.moneyFormat;

      if (!format) {
        return new Intl.NumberFormat(document.documentElement.lang || 'de-CH', {
          style: 'currency',
          currency: window.Shopify?.currency?.active || 'CHF'
        }).format(Number(cents) / 100);
      }

      const placeholder = format.match(/\{\{\s*(\w+)\s*\}\}/)?.[1] || 'amount';
      const value = Number(cents || 0);

      const formatWithDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
        const n = (number / 100).toFixed(precision);
        const parts = n.split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
        return parts.length > 1 ? parts[0] + decimal + parts[1] : parts[0];
      };

      const values = {
        amount: formatWithDelimiters(value, 2, ',', '.'),
        amount_no_decimals: formatWithDelimiters(value, 0, ',', '.'),
        amount_with_comma_separator: formatWithDelimiters(value, 2, '.', ','),
        amount_no_decimals_with_comma_separator: formatWithDelimiters(value, 0, '.', ','),
        amount_with_space_separator: formatWithDelimiters(value, 2, ' ', ','),
        amount_no_decimals_with_space_separator: formatWithDelimiters(value, 0, ' ', ','),
        amount_with_apostrophe_separator: formatWithDelimiters(value, 2, "'", '.')
      };

      return format.replace(/\{\{\s*\w+\s*\}\}/, values[placeholder] || values.amount);
    }

    escapeHtml(value) {
      return String(value)
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#039;');
    }

    escapeAttribute(value) {
      return this.escapeHtml(value);
    }
  }

  customElements.define('fendo-cart-recommendations', FendoCartRecommendations);
}
